import type {
  AttributeCollectionId,
  AttributeGroupId,
  AttributeId,
  FieldId,
  FieldValue,
} from "../../types";
import { parseFieldId } from "../../utils/field-id";
import type { FormStore } from "../types";

/**
 * コレクションが動的かどうかを判定する
 */
export const isCollectionDynamic = (
  state: FormStore,
  collectionId: AttributeCollectionId,
): boolean => {
  const collection = state.getCollection(collectionId);
  return Boolean(collection?.isDynamic);
};

/**
 * 動的グループのインスタンス数を取得する
 */
export const getDynamicGroupInstances = (
  state: FormStore,
  collectionId: AttributeCollectionId,
  groupId: AttributeGroupId,
): number => {
  const values = state.values;
  const instances = new Set<number>();

  values.forEach((value) => {
    const parsedFieldId = parseFieldId(value.fieldId);
    if (
      parsedFieldId?.collectionId === collectionId &&
      parsedFieldId?.groupId === groupId
    ) {
      instances.add(parsedFieldId.groupIndex);
    }
  });

  return instances.size;
};

/**
 * 動的グループを追加できるかどうかを判定する
 */
export const canAddDynamicGroup = (
  state: FormStore,
  collectionId: AttributeCollectionId,
  groupId: AttributeGroupId,
): boolean => {
  const collection = state.getCollection(collectionId);
  if (!collection) {
    return false;
  }

  const group = collection.attributeGroupSettings?.find(
    (g) => g.id === groupId,
  );
  if (!group?.isDynamic) {
    return false;
  }

  const instances = getDynamicGroupInstances(state, collectionId, groupId);
  return instances < (group.maxCount ?? Infinity);
};

/**
 * 動的属性を追加できるかどうかを判定する
 */
export const canAddDynamicAttribute = (
  state: FormStore,
  collectionId: AttributeCollectionId,
  groupId: AttributeGroupId,
  _attributeId: AttributeId,
): boolean => {
  const collection = state.getCollection(collectionId);
  if (!collection) {
    return false;
  }

  const group = collection.attributeGroupSettings?.find(
    (g) => g.id === groupId,
  );
  if (!group) {
    return false;
  }
  return false;
};

/**
 * 特定のフィールドのエラーを選択する
 *
 * @param state フォームストア状態
 * @param fieldId フィールドID
 * @returns エラーメッセージ
 */
export const getFieldError = (
  state: FormStore,
  fieldId: FieldId,
): string | null => {
  const error = state.errors.find((e) => e.fieldId === fieldId);
  return error?.message || null;
};

/**
 * 動的フィールドの値を取得するセレクター
 *
 * @param state フォームストア状態
 * @param collectionId コレクションID
 * @param groupId グループID
 * @param attributeId 属性ID
 * @returns 動的フィールドの値の配列
 */
export const getDynamicFieldValues = (
  state: FormStore,
  collectionId: AttributeCollectionId,
  groupId: AttributeGroupId,
  attributeId: AttributeId,
): FieldValue[] => {
  const values = state.values;
  const dynamicValues: FieldValue[] = [];

  values.forEach((value) => {
    const parsedFieldId = parseFieldId(value.fieldId);
    if (
      parsedFieldId?.collectionId === collectionId &&
      parsedFieldId?.groupId === groupId &&
      parsedFieldId?.attributeId === attributeId
    ) {
      dynamicValues.push(value);
    }
  });

  return dynamicValues;
};
